import { useState, useRef, useEffect, useCallback } from 'react';
import { Tooltip } from 'react-tooltip';

import excelIcon from '../../images/excel_icon.svg';

import HubSpotAttachmentModal from './HubSpotAttachmentModal.jsx';
import TemplateSelectionModal from './TemplateSelectionModal.jsx';
import styles from './FileUploadManager.module.css'

const FileUploadManager = ({
    mode,
    uploadedFiles,
    setUploadedFiles,
    uploadingFiles,
    setUploadingFiles,
    documents,
    setDocuments,
    errors,
    setErrors,
    api,
    popup,
    recordId,
    objectType,
    navigate,
    showToaster,
    allowNewFiles = (true),
    templateFiles = []
}) => {

  const [isUploadBoxDragOver, setIsUploadBoxDragOver] = useState(false);
  // Drag and drop state for signing order
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [replacingFileId, setReplacingFileId] = useState(null);
  const [replacingFileIndex, setReplacingFileIndex] = useState(null);
  const [replacingUploadingId, setReplacingUploadingId] = useState(null);
  const fileInputRef = useRef(null);
  const uploadIntervalsRef = useRef({});
  const [fileDraggingId, setFileDraggingId] = useState(null);
  const [fileDragOverId, setFileDragOverId] = useState(null);
  const [template, setTemplateOpen] = useState(false);
  const [isHubSpotOpen, setIsHubSpotOpen] = useState(false);

  const MAX_FILES = 20;
  const MAX_FILE_SIZE= 25 * 1024 * 1024; // 25MB
  const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.docx', '.xlsx', '.xls'];
  // Shared processor for file arrays (input or drop)
  const processSelectedFiles = (files) => {
    const picked = Array.from(files || []);
    if (picked.length === 0) return;

    // Clear file error if it exists
    if (errors.files) {
      setErrors(prev => ({ ...prev, files: '' }));
    }

    // Helper: check allowed type
    const isAllowed = (file) => {
      const name = (file.name || '').toLowerCase();
      const ext = name.substring(name.lastIndexOf('.'));
      const byExt = ALLOWED_EXTENSIONS.includes(ext);
      const type = (file.type || '').toLowerCase();
      const byMime =
        type === 'application/pdf' ||
        type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        type.startsWith('image/png');
        type.startsWith('image/jpeg');
      return byExt || byMime;
    };

    // Reject unsupported formats first
    const unsupported = picked.filter(f => !isAllowed(f)).map(f => f.name);
    const typeValidFiles = picked.filter(f => isAllowed(f));
    if (unsupported.length > 0) {
      showToaster('Unsupported file format', 'error');
    }

    // Filter out files exceeding size limit and notify
    const validSizeFiles = [];
    const rejectedBySize = [];
    typeValidFiles.forEach(f => {
      if (f.size <= MAX_FILE_SIZE) validSizeFiles.push(f);
      else rejectedBySize.push(f.name);
    });
    if (rejectedBySize.length > 0) {
      showToaster(`File size should not be exceed more than 25MB`, 'error');
    }

    if (replacingFileId) {
      const file = validSizeFiles[0];
      
      // If user cancelled → do NOTHING (keeps original file)
      if (!file) {
        setReplacingFileId(null);
        setReplacingFileIndex(null);
        return;
      }

      const newUploadingFile = {
        id: Date.now() + Math.random().toString(36).substring(2, 9),
        name: file.name,
        size: file.size,
        type: file.type,
        progress: 0,
        file: file,
        replaceIndex: replacingFileIndex,
        replaceId: replacingFileId
      };

      // NOW set placeholder ONLY when actual file exists
      setUploadedFiles(prev => {
        const arr = [...prev];
        const original = arr[replacingFileIndex];
        arr[replacingFileIndex] = {
          ...original,
          name: original?.name,
          status: 'replacing',
          progress: 0
        };
        return arr;
      });

      setUploadingFiles(prev => [...prev, newUploadingFile]);
      setReplacingUploadingId(newUploadingFile.id);
      simulateFileUpload(newUploadingFile);
      return;
    }

    // Enforce total file count cap
    const existingCount = uploadedFiles.length + uploadingFiles.length + templateFiles.length;
    const remainingSlots = Math.max(0, MAX_FILES - existingCount);
    const filesToAdd = validSizeFiles.slice(0, remainingSlots);
    const skippedByCount = validSizeFiles.slice(remainingSlots);
    if (remainingSlots === 0) {
      showToaster(`Maximum document count should be less than or equal to 20`, 'error');
      return;
    }
    if (skippedByCount.length > 0) {
      showToaster(`Maximum document count should be less than or equal to 20`, 'error');
    }

    const newUploadingFiles = filesToAdd.map(file => ({
      id: Date.now() + Math.random().toString(36).substring(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
      progress: 0,
      file: file
    }));

    setUploadingFiles(prev => [...prev, ...newUploadingFiles]);
    newUploadingFiles.forEach(uploadingFile => simulateFileUpload(uploadingFile));
  };

  const handleFileSelect = (e) => {
    processSelectedFiles(e.target.files);
    // Clear input value to allow re-selecting the same file
    e.target.value = '';
  };

  // Drag & drop upload handlers for upload box
  const onUploadBoxDragOver = (e) => {
    e.preventDefault();
    setIsUploadBoxDragOver(true);
    try { e.dataTransfer.dropEffect = 'copy'; }
    catch {
      // no-op: some browsers may not support setting dropEffect
    }
  };
  const onUploadBoxDragEnter = (e) => {
    e.preventDefault();
    setIsUploadBoxDragOver(true);
  };
  const onUploadBoxDragLeave = (e) => {
    e.preventDefault();
    setIsUploadBoxDragOver(false);
  };
  const onUploadBoxDrop = (e) => {
    e.preventDefault();
    setIsUploadBoxDragOver(false);
    const files = e.dataTransfer?.files;
    processSelectedFiles(files);
  };
  const simulateFileUpload = (uploadingFile) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.floor(Math.random() * 10) + 5;
      if (progress >= 100) {
        clearInterval(interval);
        progress = 100;
        // Move file from uploading to uploaded
        // Move file from uploading to uploaded with the actual file
        setUploadedFiles(prevFiles => {
          const newFile = {
            ...uploadingFile,
            progress: 100,
            status: 'success'
          };
          // If we are replacing a file, replace the placeholder at the original index
          const replaceId = uploadingFile.replaceId;
          // Prefer locating the placeholder by its original id. Numeric indices can shift
          // if other uploads finish while this upload is in progress.
          if (replaceId) {
            const arr = [...prevFiles];
            const foundIdx = arr.findIndex(f => f.id === replaceId);
            if (foundIdx !== -1 && arr[foundIdx] && arr[foundIdx].status === 'replacing') {
              arr[foundIdx] = newFile;
              return arr;
            }
            // If placeholder wasn't found (it may have been removed), append to the end
          }
          return [...prevFiles, newFile];
        });
        // Clear replacing state
        setReplacingFileId(null);
        setReplacingFileIndex(null);
        setReplacingUploadingId(null);
        // Auto-fill the filename when the first file is uploaded. Replacing a
        // file is an explicit title change, so it must also update the title
        // even when the previous file already populated it.
        setDocuments(prev => {
          if (!uploadingFile.replaceId && prev.title && prev.title.trim() !== '') return prev;

          const name = uploadingFile.name || '';
          const dotIndex = name.lastIndexOf('.');
          const baseName = dotIndex > 0 ? name.substring(0, dotIndex) : name;

          // Clear title error when we auto-fill
          if (baseName.trim()) {
            setErrors(e => ({ ...e, title: '' }));
          }

          return { ...prev, title: baseName };
        });

        // Remove from uploading files
        setUploadingFiles(prevFiles =>
          prevFiles.filter(file => file.id !== uploadingFile.id)
        );
        // Remove interval reference
        delete uploadIntervalsRef.current[uploadingFile.id];
      } else {
        // Update progress
        setUploadingFiles(prevFiles =>
          prevFiles.map(file =>
            file.id === uploadingFile.id ? { ...file, progress } : file
          )
        );
      }
    }, 500);
    // Store interval reference for cancellation
    uploadIntervalsRef.current[uploadingFile.id] = interval;
  };
  const cancelFileUpload = (fileId) => {
    // Clear the upload interval
    if (uploadIntervalsRef.current[fileId]) {
      clearInterval(uploadIntervalsRef.current[fileId]);
      delete uploadIntervalsRef.current[fileId];
    }
    // Remove the file from the uploading list and if it was a replacement,
    // restore the placeholder slot back to the original uploaded file state.
    setUploadingFiles(prevFiles => {
      const canceled = prevFiles.find(f => f.id === fileId);
      const remaining = prevFiles.filter(f => f.id !== fileId);

      if (canceled && canceled.replaceId) {
        const replaceId = canceled.replaceId;
        setUploadedFiles(prevUploaded => prevUploaded.map(f => {
          if (f.id === replaceId && f.status === 'replacing') {
            return {
              ...f,
              name: f.originalName || f.name,
              status: 'success'
            };
          }
          return f;
        }));
        // Clear any top-level replacing state if it matches
        setReplacingFileId(prev => prev === replaceId ? null : prev);
        setReplacingFileIndex(prev => prev === canceled.replaceIndex ? null : prev);
      }

      return remaining;
    });
  };

  // Reorder uploaded files by drag and drop (only within uploaded files list)
  const onFileDragStart = (e, id) => {
    setFileDraggingId(id);
    try { e.dataTransfer.effectAllowed = 'move'; } catch { void 0; }
  };
  const onFileDragOver = (e, id) => {
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'move'; } catch { void 0; }
    setFileDragOverId(id);
  };
  const onFileDrop = (e, targetId) => {
    e.preventDefault();
    if (fileDraggingId === null || targetId === null || fileDraggingId === targetId) {
      setFileDraggingId(null);
      setFileDragOverId(null);
      return;
    }
    const fromIndex = uploadedFiles.findIndex(f => f.id === fileDraggingId);
    const toIndex = uploadedFiles.findIndex(f => f.id === targetId);
    if (fromIndex === -1 || toIndex === -1) {
      setFileDraggingId(null);
      setFileDragOverId(null);
      return;
    }
    const reordered = [...uploadedFiles];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setUploadedFiles(reordered);
    setFileDraggingId(null);
    setFileDragOverId(null);
  };
  const onFileDragLeave = () => {
    setFileDragOverId(null);
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };
  const removeFile = (fileId) => {
    setUploadedFiles(prevFiles => prevFiles.filter(file => file.id !== fileId));
  };

  // Add files selected from the HubSpot attachment modal.
  // Mirrors the existing upload-limit behaviour in processSelectedFiles
  // and the title auto-fill in simulateFileUpload so HubSpot attachments
  // share a single source of truth.
  const addUploadedHubSpotFiles = async (selectedAttachmentData, cancellationSignal) => {
    if (cancellationSignal?.aborted) {
      return { addedCount: 0, skippedCount: selectedAttachmentData.length };
    }

    // Clear file error if it exists
    if (errors.files) {
      setErrors(prev => ({ ...prev, files: '' }));
    }

    // Enforce total file count cap
    const existingCount = uploadedFiles.length + uploadingFiles.length + templateFiles.length;
    const remainingSlots = Math.max(0, MAX_FILES - existingCount);
    const attachmentsToAdd = selectedAttachmentData.slice(0, remainingSlots);
    const skippedByCount = selectedAttachmentData.slice(remainingSlots);

    if (remainingSlots === 0) {
      showToaster(`Maximum document count should be less than or equal to 20`, 'error');
      return { addedCount: 0, skippedCount: selectedAttachmentData.length };
    }
    if (skippedByCount.length > 0) {
      showToaster(`Maximum document count should be less than or equal to 20`, 'error');
    }

    // Download the actual content through the authenticated backend endpoint.
    // Promise.all preserves selection order while downloads run in parallel.
    const downloadedFiles = await Promise.all(attachmentsToAdd.map(async (att) => {
      let fileName = att.fileName || `HubSpot-file-${att.fileId}`;
      if (att.extension && !fileName.toLowerCase().endsWith(att.extension.toLowerCase())) {
        fileName = `${fileName}.${att.extension}`;
      }

      try {
        const response = await api.get('/hubspot/get-attachment-files', {
          params: { fileId: att.fileId },
          responseType: 'blob',
          signal: cancellationSignal
        });

        if (response.status !== 200 || !response.data) {
          showToaster('Failed to attach hubspot document', 'error');
          throw new Error(`Failed to attach hubspot documents`);
        }

        const file = new File([response.data], fileName, {
          type: att.fileContentType || response.data.type || 'application/octet-stream'
        });

        if (file.size > MAX_FILE_SIZE) {
          showToaster(`${fileName} size should not be exceed more than 25MB`);
          throw new Error(`${fileName} exceeds the 25 MB limit`);
        }

        return {
          id: `hubspot-${att.fileId}-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
          name: file.name,
          type: file.type,
          size: file.size,
          progress: 100,
          status: 'success',
          file
        };
      } catch (error) {
        if (cancellationSignal?.aborted) {
          return null;
        }
        console.error(`Failed to upload HubSpot attachment:`, error);
        return null;
      }
    }));

    // Do not attach responses that completed after the modal was closed.
    if (cancellationSignal?.aborted) {
      return { addedCount: 0, skippedCount: selectedAttachmentData.length };
    }

    const newHubSpotFiles = downloadedFiles.filter(Boolean);
    const failedDownloads = attachmentsToAdd.length - newHubSpotFiles.length;

    if (newHubSpotFiles.length === 0) {
      return { addedCount: 0, skippedCount: skippedByCount.length + failedDownloads };
    }

    setUploadedFiles(prev => [...prev, ...newHubSpotFiles]);

    // Auto-fill title with first added filename (without extension) if title is empty
    setDocuments(prev => {
      if (prev.title && prev.title.trim() !== '') return prev;
      const firstFileName = newHubSpotFiles[0]?.name || '';
      const dotIndex = firstFileName.lastIndexOf('.');
      const baseName = dotIndex > 0 ? firstFileName.substring(0, dotIndex) : firstFileName;
      if (baseName.trim()) {
        setErrors(e => ({ ...e, title: '' }));
      }
      return { ...prev, title: baseName };
    });

    return {
      addedCount: newHubSpotFiles.length,
      skippedCount: skippedByCount.length + failedDownloads,
    };
  };
  
  const replaceFile = (fileId) => {
    const idx = uploadedFiles.findIndex(f => f.id === fileId);
    if (idx === -1) return;
    setReplacingFileIndex(idx);
    setReplacingFileId(fileId);

    fileInputRef.current.click();
  };
  
  const getFileIcon = (fileType) => {
    if (fileType.includes('pdf')) {
      return <img src="https://static.boldsign.com/202507081507-260076/img/661f0f2.svg" alt='pdf-icon' width={'32px'} height={'38px'}></img>;
    } else if (fileType.includes('image')) {
      return <img src="https://static.boldsign.com/202507081507-260076/img/46fb0b7.svg" alt='image-icon' width={'32px'} height={'38px'}></img>;
    } else if (fileType.includes('word') || fileType.includes('docx')) {
      return <img src="https://static.boldsign.com/202507081507-260076/img/ab4aec6.svg"alt='word-icon' width={'32px'} height={'38px'} ></img>;
    } else if (fileType.includes('sheet') || fileType.includes('xlsx') || fileType.includes('xls')) {
      return <img src={excelIcon} alt='excel-icon' width={'32px'} height={'38px'} ></img>;
    } else {
      return 'icon';
    }
  };

  return (
    <div className="contentBox">
        <div className="contentBoxHeader">Add file(s)</div>
        <div className={styles.fileUploadOptions}>
            <div className="upload-option">
                <div
                    className={`upload-box ${errors.files ? 'error' : ''} ${isUploadBoxDragOver ? 'drag-active' : ''}`}
                    onDragOver={onUploadBoxDragOver}
                    onDragEnter={onUploadBoxDragEnter}
                    onDragLeave={onUploadBoxDragLeave}
                    onDrop={onUploadBoxDrop}
                >
                    <p className="upload-title">Choose from computer</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx,.xls"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleFileSelect}
                    />
                    <button className="browse-button" onClick={triggerFileInput}>Browse File</button>
                    <p className="supported-formats">Preferred format: PDF</p>
                </div>
                {errors.files && <div className="error-message">{errors.files}</div>}
            </div>
                    {(mode === 'documentSend' || mode === 'templateSend') && (
                      <>
                      <div className={styles.orDivider}>
                        <svg className={styles.svg} viewBox="0 0 40 120" aria-hidden="true">
                          <line x1="20" y1="0" x2="20" y2="40" className={styles.line} />
                          <line x1="20" y1="80" x2="20" y2="120" className={styles.line} />
                          <text x="20" y="60" textAnchor="middle" dominantBaseline="middle" className={styles.text}>
                            OR
                          </text>
                        </svg>
                    </div>
                    <div className={`${styles.browseTemplates}`}>
                      <p className="upload-title">Choose from HubSpot</p>
                      <button 
                        className="browse-button-flexible" 
                        onClick={() => setIsHubSpotOpen(true)}
                        disabled={!recordId || !objectType}
                        title={!recordId || !objectType ? 'HubSpot record information not available' : 'Browse attachments from this HubSpot record'}
                      >
                        Browse Attachments
                      </button>
                    </div>
                    </>)}
                    {mode === 'documentSend' && (
                      <><div className={styles.orDivider}>
                        <svg className={styles.svg} viewBox="0 0 40 120" aria-hidden="true">
                          <line x1="20" y1="0" x2="20" y2="40" className={styles.line} />
                          <line x1="20" y1="80" x2="20" y2="120" className={styles.line} />
                          <text x="20" y="60" textAnchor="middle" dominantBaseline="middle" className={styles.text}>
                            OR
                          </text>
                        </svg>
                    </div>
                    <div className={styles.browseTemplates}>
                        <p className="upload-title">Choose from Templates</p>
                        <button className="browse-button-flexible" onClick={() => setTemplateOpen(true)} >Browse Templates</button>
                    </div>
                    </>
                  )}
        </div>

        {/* Template Files List (read-only, mode=templateSend only) */}
        {mode === 'templateSend' && templateFiles.length > 0 && (
          <div className="uploaded-files-container">
            <div className='field-group'>
              <label htmlFor='templateFiles'>
              Template Files</label>
            </div>
            {templateFiles.map((file, index) => {
              // Extract file extension from name for icon display
              const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
              let fileType = '';
              if (fileExt === '.pdf') fileType = 'application/pdf';
              else if (['.jpg', '.jpeg', '.png'].includes(fileExt)) fileType = 'image/png';
              else if (fileExt === '.docx') fileType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
              else if (['.xlsx', '.xls'].includes(fileExt)) fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
              
              return (
                <div key={file.id} className="uploaded-file-item">
                  <div className="file-number">{index + 1}</div>
                  <div className="file-icon">{getFileIcon(fileType || fileExt)}</div>
                  <div className="file-details">
                      <div className="file-name" data-tooltip-id={`template-file-tooltip-${file.id}`}>{file.name}</div>
                      <Tooltip id={`template-file-tooltip-${file.id}`} content={file.name} place="top" className="tooltip-info" />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Uploaded Files List (for non-templateSend modes) */}
        {(uploadedFiles.length > 0 || uploadingFiles.length > 0) && (
            <div className="uploaded-files-container">
                {errors.progressError && <div className="error-message">{errors.progressError}</div>}
                {mode === 'templateSend' && allowNewFiles && (<div className='field-group'>
                  <label htmlFor='templateFiles'>Additional Files</label>
                </div>)}
                {uploadedFiles.map((file, index) => {
                    // If this slot is a placeholder for replacement, show inline uploading progress
                    if (file.status === 'replacing') {
                        const uploadingMatch = uploadingFiles.find(u => u.replaceId === file.id);
                        const progress = uploadingMatch ? uploadingMatch.progress : 0;
                        return (
                            <div
                                key={file.id}
                                className={`uploaded-file-item ${fileDragOverId === file.id ? 'drag-over' : ''}`}
                                draggable
                                onDragStart={(e) => onFileDragStart(e, file.id)}
                                onDragOver={(e) => onFileDragOver(e, file.id)}
                                onDrop={(e) => onFileDrop(e, file.id)}
                                onDragEnter={() => setFileDragOverId(file.id)}
                                onDragLeave={onFileDragLeave}
                            >
                                <div className="file-number">{templateFiles?.length + index + 1}</div>
                                <div className="file-icon">{getFileIcon(file.type)}</div>
                                <div className="file-details">
                                  <div className="file-name" data-tooltip-id={`file-tooltip-${file.id}`} data-tooltip-content={file.name}>{file.name}</div>
                                  <Tooltip id={`file-tooltip-${file.id}`} place="top" className="tooltip-info" />
                                </div>
                                <div className="file-upload-progress">
                                    <div className="progress-bar">
                                      <div
                                        className="progress-fill"
                                        style={{ width: `${progress}%` }}
                                      ></div>
                                    </div>
                                    <div className="progress-text">Processing {progress}%</div>
                                    {uploadingMatch && (
                                      <button
                                        className="cancel-upload-button"
                                        onClick={() => cancelFileUpload(uploadingMatch.id)}
                                      >
                                        <span className={`${styles.sfIconBase} ${styles.sfCloseIcon}`}></span>
                                      </button>
                                    )}
                                </div>
                            </div>
                        );
                    }
                    return (
                        <div
                          key={file.id}
                          className={`uploaded-file-item ${fileDragOverId === file.id ? 'drag-over' : ''}`}
                          draggable
                          onDragStart={(e) => onFileDragStart(e, file.id)}
                          onDragOver={(e) => onFileDragOver(e, file.id)}
                          onDrop={(e) => onFileDrop(e, file.id)}
                          onDragEnter={() => setFileDragOverId(file.id)}
                          onDragLeave={onFileDragLeave}
                        >
                            <div className="file-number">{templateFiles?.length + index + 1}</div>
                            <div className="file-icon">{getFileIcon(file.type)}</div>
                            <div className="file-details">
                              <div className="file-name" data-tooltip-id={`file-tooltip-${file.id}`}>{file.name}</div>
                              <Tooltip id={`file-tooltip-${file.id}`} content={file.name} place="top" className="tooltip-info" />
                            </div>
                            <div className="file-status success">Uploaded Successfully</div>
                            {((mode === 'templateSend' && allowNewFiles) || (mode === 'documentSend' || mode === 'templateCreate')) && (<div className="file-actions">
                              <button className="action-button replace-button" onClick={() => replaceFile(file.id)}>
                                <span className="sfIconBase sfReplaceIcon"></span>
                                Replace
                              </button>
                              <button className="action-button remove-button" onClick={() => removeFile(file.id)}>
                                <span className="sfIconBase sfDeleteIcon"></span>
                                Remove
                              </button>
                            </div>)}
                        </div>
                    );
                })}
                {uploadingFiles.filter(f => !f.replaceId).map((file, index) => (
                    <div key={file.id} className="uploaded-file-item uploading">
                        <div className="file-number">{templateFiles?.length + uploadedFiles.length + index + 1}</div>
                        <div className={`file-icon ${getFileIcon(file.type)}`}></div>
                        <div className="file-details">
                            <div className="file-name" data-tooltip-id={`file-tooltip-${file.id}`}>{file.name}</div>
                              <Tooltip id={`file-tooltip-${file.id}`} content={file.name} place="top" className="tooltip-info" />
                        </div>
                        <div className="file-upload-progress">
                            <div className="progress-bar">
                              <div
                                className="progress-fill"
                                style={{ width: `${file.progress}%` }}
                              ></div>
                            </div>
                            <div className="progress-text">Processing {file.progress}%</div>
                            <button
                              className="cancel-upload-button"
                              onClick={() => cancelFileUpload(file.id)}
                            >
                              <span className="sfIconBase sfCloseIcon"></span>
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        )}
        <HubSpotAttachmentModal
            isOpen={isHubSpotOpen}
            onClose={() => setIsHubSpotOpen(false)}
            api={api}
            popup={popup}
            recordId={recordId}
            objectType={objectType}
            showToaster={showToaster}
            maxSelectableAttachments={Math.max(0, MAX_FILES - uploadedFiles.length - uploadingFiles.length - templateFiles.length)}
            addUploadedHubSpotFiles={addUploadedHubSpotFiles}
        />

        <TemplateSelectionModal
            isOpen={template}
            onClose={() => setTemplateOpen(false)}
            api={api}
            popup={popup}
            navigate={navigate}
            objectType={objectType}
            recordId={recordId}
        />
    </div>
  );
};

export default FileUploadManager;